import React from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation} from 'react-router-dom';

import {SEO_BASE_URL, seoMetaList, SeoMeta} from '../../routes/seo';

function matchSeoMeta(pathname: string): SeoMeta | undefined {
    const exact = seoMetaList.find(m => !m.isDynamic && m.path === pathname);
    if (exact) return exact;

    return seoMetaList.find(m => {
        if (!m.isDynamic) return false;
        const pattern = m.path
            .split('/')
            .map(seg => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
            .join('/');
        return new RegExp(`^${pattern}$`).test(pathname);
    });
}

export function RouteSEO() {
    const {pathname} = useLocation();
    const meta = matchSeoMeta(pathname);

    if (!meta || !meta.isDynamic) {
        return null;
    }

    const canonicalPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    const canonical = `${SEO_BASE_URL}${canonicalPath}`;

    return (
        <Helmet>
            <title>{meta.title}</title>
            <meta name='description' content={meta.description} />
            <meta name='robots' content={meta.robots} />
            <link rel='canonical' href={canonical} />
            <meta property='og:title' content={meta.title} />
            <meta property='og:description' content={meta.description} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={canonical} />
            {meta.ogImage && <meta property='og:image' content={meta.ogImage} />}
            {meta.ogImage && <meta property='og:image:width' content='1200' />}
            {meta.ogImage && <meta property='og:image:height' content='630' />}
            {meta.ogImage && <meta property='og:image:type' content='image/png' />}
            {meta.ogImageAlt && <meta property='og:image:alt' content={meta.ogImageAlt} />}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:url' content={canonical} />
            <meta name='twitter:title' content={meta.title} />
            <meta name='twitter:description' content={meta.description} />
            {meta.ogImage && <meta name='twitter:image' content={meta.ogImage} />}
            {meta.ogImageAlt && <meta name='twitter:image:alt' content={meta.ogImageAlt} />}
        </Helmet>
    );
}

export default RouteSEO;
